import type { FormConfig } from '@risksmart-app/shared/forms/types';

import { SUPPORTED_DISPLAY_TYPES } from './coerceFieldValue';
import type { ActiveFormDescriptorField } from './types';

/**
 * `displayType` is OPTIONAL on a form-config field and every other consumer
 * treats "absent" as `text`. Prefill cannot: across the register form configs,
 * "absent" covers user/group pickers (`Owners` on impact, `Owner` on control
 * group, `approvedBy`/`requestedBy`, `CompletedBy`), radio booleans
 * (`requireReattestation`), selects (`AppetiteType`, `Adherence`,
 * `attestationTimeLimit`), rich-text editors (`Content`, `RatingGuidance`),
 * numeric inputs (`Cost`, `CostValue`) and entity references (`*Id` / `*Ids`,
 * `linkedDocuments`) just as much as it covers `Title`.
 *
 * So untyped fields are FAIL-CLOSED: a field with no `displayType` is published
 * only if it appears below. Everything else untyped is skipped, which means a
 * field newly added to a shared config is never silently prefillable — the cost
 * of omission is "not prefillable yet", the cost of the old default was writing
 * a bare string into a relational or boolean field.
 *
 * Membership is decided by the CONTROL that renders the field: every key here is
 * rendered by `ControlledInput` / `ControlledTextarea` / `ControlledAutosuggest`
 * / `ControlledLinkInput` (all plain-string values) in a register form. Add a key
 * only after checking that, or give the field an explicit `displayType` in its
 * form config instead — which is the better fix.
 */
const UNTYPED_TEXT_FIELD_KEYS = new Set([
  // Shared across most registers.
  'Title',
  'title', // third_party canonically lowercases its standard field ids
  'Name',
  'Description',
  'Summary',
  // document / document_file
  'Version',
  'Link',
  'ReasonForReview',
  // internal_audit_entity
  'BusinessArea',
  // asset
  'SerialNumber',
  'Location',
  'DepreciationMethod',
  'AssetType',
  // indicator / indicator_result
  'TargetValueTxt',
  'Unit',
  // attestation_cycle
  'attestationPromptText',
  // third_party
  'address',
  'cityTown',
  'postcode',
  'country',
  'companyName',
  'contactName',
  'primaryContactName',
  // NOT listed, though they are plain text inputs: `contactEmail`,
  // `companyDomain` and `companiesHouseNumber`. These are externally verifiable
  // identifiers — a fabricated value looks authoritative, reads as plausible in
  // the proposal card, and is costly to detect once saved. Nobody needs a model
  // to draft them. Keep them out even though the type is safe.
]);

const isSupportedType = (displayType: string): boolean =>
  (SUPPORTED_DISPLAY_TYPES as readonly string[]).includes(displayType);

/**
 * Build the prefillable-field descriptor for the active form: the shared form
 * config intersected with the fields actually mounted (`allFieldIds` from
 * `RiskSmartFormProvider`), filtered to the display types this cut can coerce.
 *
 * Pure — the publisher supplies the config, the live field ids, and the set of
 * required field ids. A field with no `displayType` is emitted only if it is an
 * explicitly-known text field (see `UNTYPED_TEXT_FIELD_KEYS`).
 */
export const buildActiveFormDescriptor = (
  formConfig: FormConfig,
  allFieldIds: string[],
  requiredFieldIds: Set<string>
): ActiveFormDescriptorField[] => {
  const descriptor: ActiveFormDescriptorField[] = [];

  for (const fieldId of allFieldIds) {
    // Custom attributes register under a nested path (`CustomAttributeData.<key>`)
    // and use non-stable per-org keys — never prefillable in this cut.
    if (fieldId.startsWith('CustomAttributeData')) {
      continue;
    }
    const config = formConfig[fieldId];
    // No config entry → a derived/unknown field (e.g. `ancestorContributors`).
    if (!config) {
      continue;
    }

    const declaredType = config.displayType?.displayType;
    if (declaredType === undefined) {
      // Untyped: prefillable only for known plain-text keys (fail-closed).
      if (!UNTYPED_TEXT_FIELD_KEYS.has(fieldId)) {
        continue;
      }
    } else if (!isSupportedType(declaredType)) {
      continue;
    }

    const displayType = declaredType ?? 'text';

    descriptor.push({
      key: fieldId,
      label: config.formLabel,
      displayType,
      required: requiredFieldIds.has(fieldId),
    });
  }

  return descriptor;
};
